import type { IEntry } from '@app/domain/instrument/instrument.interface'
import type { IPatient } from '@app/domain/patient/patient.interface'
import type {
  IVisitDefinition,
  IVisit,
  VisitStatusId,
  VisitId,
} from '@app/domain/visit/visit.interface'
import type { AppAction } from '@app/state'

type InstrumentId = string

export enum VisitStage {
  Collection = 'collection',
  Completed = 'completed',
  Signature = 'signature',
}

export interface IVisitState {
  workflowStep: number
  screen: number
  stage: VisitStage
  entries: Record<InstrumentId, IEntry>
  display: Record<string, boolean>
  progress?: number
}

export interface State {
  visit: IVisit
  visitStatusOnLoad?: VisitStatusId
  definition: IVisitDefinition
  patient?: IPatient
  currentState: IVisitState
  loadStatus?: string
  authStatus?: string
  witnessStatus?: string
  restartVisitFromBeginning: boolean
}

export enum ActionType {
  ClearContext = 'INTAKE/CLEAR_CONTEXT',
  LoadVisit = 'INTAKE/LOAD_VISIT',
  LoadVisitPending = 'INTAKE/LOAD_VISIT/PENDING',
  LoadVisitSucceeded = 'INTAKE/LOAD_VISIT/SUCCEEDED',
  LoadVisitFailed = 'INTAKE/LOAD_VISIT/FAILED',
  VisitRetrieved = 'INTAKE/VISIT_RETRIEVED',
  GetEntries = 'INTAKE/GET_ENTRIES',
  EntriesRetrieved = 'INTAKE/ENTRIES_RETRIEVED',
  SubmitResponses = 'INTAKE/SUBMIT_RESPONSES',
  EntryUpdated = 'INTAKE/ENTRY_UPDATED',
  Continue = 'INTAKE/CONTINUE',
  Next = 'INTAKE/NEXT',
  GoBack = 'INTAKE/GO_BACK',
  Previous = 'INTAKE/PREVIOUS',
  Resume = 'INTAKE/RESUME',
  SetRestartFromBeginning = 'INTAKE/RESUME/SET_FROM_BEGINNING',
  EvaluateDisplayLogic = 'INTAKE/EVALUATE_DISPLAY_LOGIC',
  DisplayLogicEvaluated = 'INTAKE/DISPLAY_LOGIC_EVALUATED',
  AuthenticateResponses = 'INTAKE/AUTHENTICATE_RESPONSES',
  AuthenticateResponsesStarted = 'INTAKE/AUTHENTICATE_RESPONSES/STARTED',
  AuthenticateResponsesSuccess = 'INTAKE/AUTHENTICATE_RESPONSES/SUCCESS',
  AuthenticateResponsesFailure = 'INTAKE/AUTHENTICATE_RESPONSES/FAILURE',
  WitnessResponses = 'INTAKE/WITNESS_RESPONSES',
  WitnessResponsesStarted = 'INTAKE/WITNESS_RESPONSES/STARTED',
  WitnessResponsesSuccess = 'INTAKE/WITNESS_RESPONSES/SUCCESS',
  WitnessResponsesFailure = 'INTAKE/WITNESS_RESPONSES/FAILURE',
  UpdateVisitStatus = 'INTAKE/UPDATE_VISIT_STATUS',
  UpdateVisitStatusSuccess = 'INTAKE/UPDATE_VISIT_STATUS/SUCCESS',
  UpdateVisitStatusFailure = 'INTAKE/UPDATE_VISIT_STATUS/FAILURE',
  UpdateVisitStatusOnLoad = 'INTAKE/UPDATE_VISIT_STATUS_ON_LOAD',
  Restart = 'INTAKE/RESTART',
  CloseSurvey = 'INTAKE/CLOSE_SURVEY',
}

export type Action =
  | { type: ActionType.ClearContext }
  | { type: ActionType.LoadVisit; visitId: VisitId }
  | { type: ActionType.LoadVisitPending }
  | { type: ActionType.LoadVisitSucceeded }
  | { type: ActionType.LoadVisitFailed }
  | {
      type: ActionType.VisitRetrieved
      visit: IVisit
      definition: IVisitDefinition
    }
  | { type: ActionType.GetEntries }
  | { type: ActionType.EntriesRetrieved; entries: Array<IEntry> }
  | {
      type: ActionType.SubmitResponses
      entryId: string
      responses: Record<string, string>
      preventNextScreen: boolean
    }
  | { type: ActionType.EntryUpdated; entry: IEntry }
  | { type: ActionType.GoBack }
  | { type: ActionType.Continue }
  | { type: ActionType.Next; nextState: IVisitState }
  | { type: ActionType.Previous; prevState: IVisitState }
  | { type: ActionType.Resume; state: IVisitState }
  | { type: ActionType.SetRestartFromBeginning; restartFromBeginning: boolean }
  | {
      type: ActionType.EvaluateDisplayLogic
      instrumentId: string
      questionId?: string
      response?: string
    }
  | {
      type: ActionType.DisplayLogicEvaluated
      displayLogicResults: Record<string, boolean>
      instrumentId: string
    }
  | {
      type: ActionType.AuthenticateResponses
      patient: IPatient
      signature: string
    }
  | { type: ActionType.AuthenticateResponsesStarted }
  | { type: ActionType.AuthenticateResponsesSuccess }
  | { type: ActionType.AuthenticateResponsesFailure; error: string }
  | { type: ActionType.WitnessResponses; pin: string; signature: string }
  | { type: ActionType.WitnessResponsesStarted }
  | { type: ActionType.WitnessResponsesSuccess }
  | { type: ActionType.WitnessResponsesFailure; error: string }
  | { type: ActionType.UpdateVisitStatus; visitId: VisitId; status: string }
  | {
      type: ActionType.UpdateVisitStatusOnLoad
      visitId: VisitId
      status: VisitStatusId
    }
  | { type: ActionType.UpdateVisitStatusSuccess; visit: IVisit }
  | { type: ActionType.UpdateVisitStatusFailure; error: string }
  | { type: ActionType.CloseSurvey; route?: string }
  | { type: ActionType.Restart }

export const actions = {
  clearContext(): Action {
    return { type: ActionType.ClearContext }
  },
  loadVisit(id: string): Action {
    return { type: ActionType.LoadVisit, visitId: id }
  },
  loadVisitPending(): Action {
    return { type: ActionType.LoadVisitPending }
  },
  loadVisitSucceeded(): Action {
    return { type: ActionType.LoadVisitSucceeded }
  },
  loadVisitFailed(): Action {
    return { type: ActionType.LoadVisitFailed }
  },
  visitRetrieved(visit: IVisit, definition: IVisitDefinition): Action {
    return { type: ActionType.VisitRetrieved, visit, definition }
  },
  getEntries(): Action {
    return { type: ActionType.GetEntries }
  },
  entriesRetrieved(entries: Array<IEntry>): Action {
    return { type: ActionType.EntriesRetrieved, entries }
  },
  submitResponses(
    entryId: string,
    responses: Record<string, string>,
    preventNextScreen: boolean,
  ): Action {
    return {
      type: ActionType.SubmitResponses,
      entryId,
      responses,
      preventNextScreen,
    }
  },
  entryUpdated(entry: IEntry): Action {
    return { type: ActionType.EntryUpdated, entry }
  },
  continue(): Action {
    return { type: ActionType.Continue }
  },
  next(nextState: IVisitState): Action {
    return { type: ActionType.Next, nextState }
  },
  goBack(): Action {
    return { type: ActionType.GoBack }
  },
  previous(prevState: IVisitState): Action {
    return { type: ActionType.Previous, prevState: prevState }
  },
  resume(state: IVisitState): Action {
    return { type: ActionType.Resume, state }
  },
  restart(): Action {
    return { type: ActionType.Restart }
  },
  setRestartFromBeginning(restartFromBeginning: boolean): Action {
    return { type: ActionType.SetRestartFromBeginning, restartFromBeginning }
  },
  evaluateDisplayLogic(
    instrumentId: string,
    questionId?: string,
    response?: string,
  ) {
    return {
      type: ActionType.EvaluateDisplayLogic,
      instrumentId,
      questionId,
      response,
    }
  },
  displayLogicEvaluated(
    displayLogicResults: Record<string, boolean>,
    instrumentId: string,
  ) {
    return {
      type: ActionType.DisplayLogicEvaluated,
      displayLogicResults,
      instrumentId,
    }
  },
  authenticateResponses(patient: IPatient, signature: string) {
    return { type: ActionType.AuthenticateResponses, patient, signature }
  },
  authenticateResponsesStarted() {
    return { type: ActionType.AuthenticateResponsesStarted }
  },
  authenticateResponsesSuccess() {
    return { type: ActionType.AuthenticateResponsesSuccess }
  },
  authenticateResponsesFailure(error: string) {
    return { type: ActionType.AuthenticateResponsesFailure, error }
  },
  witnessResponses(pin: string, signature: string) {
    return { type: ActionType.WitnessResponses, pin, signature }
  },
  witnessResponsesStarted() {
    return { type: ActionType.WitnessResponsesStarted }
  },
  witnessResponsesSuccess() {
    return { type: ActionType.WitnessResponsesSuccess }
  },
  witnessResponsesFailed(error: string) {
    return { type: ActionType.WitnessResponsesFailure, error }
  },
  updateVisitStatus(visitId: VisitId, status: string) {
    return { type: ActionType.UpdateVisitStatus, visitId, status }
  },
  updateVisitStatusSuccess(visit: IVisit) {
    return { type: ActionType.UpdateVisitStatusSuccess, visit }
  },
  updateVisitStatusFailure(error: string) {
    return { type: ActionType.UpdateVisitStatusFailure, error }
  },
  updateVisitStatusOnLoad(visitId: VisitId, status: VisitStatusId) {
    return { type: ActionType.UpdateVisitStatusOnLoad, visitId, status }
  },
  closeSurvey(route?: string) {
    return { type: ActionType.CloseSurvey, route }
  },
}

export const initialState: State = {
  visit: undefined,
  visitStatusOnLoad: undefined,
  definition: undefined,
  patient: undefined,
  loadStatus: undefined,
  currentState: {
    workflowStep: 0,
    screen: 0,
    stage: VisitStage.Collection,
    entries: {},
    display: {},
    progress: 1,
  },
  restartVisitFromBeginning: false,
}

export function reducer(state: State = initialState, action: AppAction): State {
  switch (action.type) {
    case ActionType.ClearContext:
      return initialState
    case ActionType.LoadVisitPending:
      return {
        ...state,
        loadStatus: 'pending',
      }
    case ActionType.LoadVisitSucceeded:
      return {
        ...state,
        loadStatus: 'succeeded',
      }
    case ActionType.Resume:
      return {
        ...state,
        currentState: action.state,
      }
    case ActionType.SetRestartFromBeginning:
      return {
        ...state,
        restartVisitFromBeginning: action.restartFromBeginning,
      }
    case ActionType.LoadVisitFailed:
      return {
        ...state,
        loadStatus: 'failed',
      }
    case ActionType.VisitRetrieved:
      return {
        ...state,
        visit: action.visit,
        definition: action.definition,
      }
    case ActionType.EntriesRetrieved: {
      const entries = state.currentState.entries
      action.entries.forEach((entry) => {
        entries[entry.instrumentId] = entry
      })
      const newState = {
        ...state.currentState,
        entries: entries,
      }
      return {
        ...state,
        currentState: newState,
      }
    }
    case ActionType.SubmitResponses:
      return {
        ...state,
      }
    case ActionType.EntryUpdated: {
      const updatedEntries = {
        ...state.currentState.entries,
        [action.entry.instrumentId]: action.entry,
      }

      return {
        ...state,
        currentState: {
          ...state.currentState,
          entries: updatedEntries,
        },
      }
    }
    case ActionType.Next:
      return {
        ...state,
        currentState: action.nextState,
      }
    case ActionType.Previous:
      return {
        ...state,
        currentState: action.prevState,
      }
    case ActionType.DisplayLogicEvaluated: {
      const newState = {
        ...state.currentState,
        display: action.displayLogicResults,
      }
      return {
        ...state,
        currentState: newState,
      }
    }
    case ActionType.AuthenticateResponses:
      return {
        ...state,
      }
    case ActionType.AuthenticateResponsesStarted:
      return {
        ...state,
        authStatus: 'pending',
      }
    case ActionType.AuthenticateResponsesSuccess:
      return {
        ...state,
        authStatus: 'success',
      }
    case ActionType.AuthenticateResponsesFailure:
      return {
        ...state,
        authStatus: 'failure',
      }
    case ActionType.WitnessResponsesStarted:
      return {
        ...state,
        witnessStatus: 'pending',
      }
    case ActionType.WitnessResponsesSuccess:
      return {
        ...state,
        witnessStatus: 'success',
      }
    case ActionType.WitnessResponsesFailure:
      return {
        ...state,
        witnessStatus: 'failure',
      }
    case ActionType.UpdateVisitStatusSuccess:
      return {
        ...state,
        visit: {
          ...state.visit,
          status: action.visit.status,
        },
      }
    case ActionType.UpdateVisitStatusOnLoad:
      return {
        ...state,
        visitStatusOnLoad: action.status,
      }
    default:
      return state
  }
}

export const queries = {
  isFirstPage(state: State) {
    return (
      state.currentState.workflowStep === 0 && state.currentState.screen === 0
    )
  },
}
